import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Dialog, Flex, RadioGroup, Text, TextArea } from '@radix-ui/themes'
import { useTRPC } from '~/utils/trpc'
import { MessageCallout } from '~/components/MessageCallout'
import { RETIREMENT_REASON_PRESETS } from '~/features/installations/constants'

const CUSTOM_REASON = '__custom__'

interface RetireInstallationDialogProps {
  installationId: number
  children: ReactNode
}

export const RetireInstallationDialog = ({ installationId, children }: RetireInstallationDialogProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [choice, setChoice] = useState('')
  const [customReason, setCustomReason] = useState('')

  const retireMutation = useMutation(
    trpc.ops.stationInstallations.retire.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.ops.stationInstallations.get.queryKey({ installationId })
        })
        queryClient.invalidateQueries({
          queryKey: trpc.ops.stationInstallations.events.list.queryKey({ installationId })
        })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        // Retiring resolves any open stall, so the stalled worklist is stale too.
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.listStalled.queryKey() })
        setOpen(false)
      }
    })
  )

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setChoice('')
      setCustomReason('')
      retireMutation.reset()
    }
    setOpen(next)
  }

  const reason = choice === CUSTOM_REASON ? customReason.trim() : choice
  const pending = retireMutation.isPending

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>Retire installation #{installationId}</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Records why this installation stops here, and adds it to the timeline with your name.
        </Dialog.Description>

        <Flex direction="column" gap="4">
          <RadioGroup.Root value={choice} disabled={pending} onValueChange={setChoice}>
            <Flex direction="column" gap="2">
              {[...RETIREMENT_REASON_PRESETS, CUSTOM_REASON].map((value) => (
                <Text as="label" size="2" key={value}>
                  <Flex gap="2" align="center">
                    <RadioGroup.Item value={value} />
                    {value === CUSTOM_REASON ? 'Custom' : value}
                  </Flex>
                </Text>
              ))}
            </Flex>
          </RadioGroup.Root>

          {choice === CUSTOM_REASON ? (
            <TextArea
              placeholder="Why is this installation being retired?"
              value={customReason}
              disabled={pending}
              onChange={(e) => setCustomReason(e.target.value)}
            />
          ) : null}

          {retireMutation.error ? <MessageCallout color="red" message={retireMutation.error.message} /> : null}
        </Flex>

        <Flex justify="end" gap="2" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={pending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            variant="solid"
            color="red"
            loading={pending}
            disabled={reason.length === 0}
            onClick={() => retireMutation.mutate({ installationId, reason })}
          >
            Retire installation
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
